/**
 * Schedule Template Library
 *
 * Pre-built schedule templates for common building types and use cases.
 * Based on ASHRAE 90.1, DOE Reference Buildings, and industry standards.
 */

import type { ScheduleTemplate, TimeValue } from './types';

/**
 * Helper to create time values from "HH:MM" string format
 */
function tv(until: string, value: number): TimeValue {
  const parts = until.split(':').map(Number);
  return { hour: parts[0] ?? 0, minute: parts[1] ?? 0, value };
}

/**
 * Office building occupancy schedule (standard 8am-6pm)
 */
const officeOccupancyStandard: ScheduleTemplate = {
  id: 'office-occupancy-standard',
  name: 'Office Occupancy (Standard)',
  description: '8am-6pm weekday operation with lunch break',
  category: 'occupancy',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('13:00', 0.8),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0),
    ],
    tuesday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('13:00', 0.8),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0),
    ],
    wednesday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('13:00', 0.8),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0),
    ],
    thursday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('13:00', 0.8),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0),
    ],
    friday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('13:00', 0.8),
      tv('17:00', 0.9),
      tv('18:00', 0.3),
      tv('24:00', 0),
    ],
    saturday: [tv('24:00', 0)],
    sunday: [tv('24:00', 0)],
    holiday: [tv('24:00', 0)],
    summerDesignDay: [
      tv('06:00', 0),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('24:00', 0),
    ],
    winterDesignDay: [
      tv('06:00', 0),
      tv('08:00', 0.5),
      tv('12:00', 1.0),
      tv('17:00', 1.0),
      tv('18:00', 0.5),
      tv('24:00', 0),
    ],
  },
};

/**
 * Office building lighting schedule
 */
const officeLighting: ScheduleTemplate = {
  id: 'office-lighting',
  name: 'Office Lighting',
  description: 'Daylight-responsive office lighting schedule',
  category: 'lighting',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Fraction',
  source: 'ASHRAE 90.1',
  weekPattern: {
    monday: [
      tv('06:00', 0.05),
      tv('07:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0.05),
    ],
    tuesday: [
      tv('06:00', 0.05),
      tv('07:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0.05),
    ],
    wednesday: [
      tv('06:00', 0.05),
      tv('07:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0.05),
    ],
    thursday: [
      tv('06:00', 0.05),
      tv('07:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('21:00', 0.1),
      tv('24:00', 0.05),
    ],
    friday: [
      tv('06:00', 0.05),
      tv('07:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.3),
      tv('24:00', 0.05),
    ],
    saturday: [tv('24:00', 0.05)],
    sunday: [tv('24:00', 0.05)],
    holiday: [tv('24:00', 0.05)],
  },
};

/**
 * Office equipment/plug loads schedule
 */
const officeEquipment: ScheduleTemplate = {
  id: 'office-equipment',
  name: 'Office Equipment',
  description: 'Office plug loads and equipment schedule',
  category: 'equipment',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('24:00', 0.3),
    ],
    tuesday: [
      tv('06:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('24:00', 0.3),
    ],
    wednesday: [
      tv('06:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('24:00', 0.3),
    ],
    thursday: [
      tv('06:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('24:00', 0.3),
    ],
    friday: [
      tv('06:00', 0.3),
      tv('08:00', 0.9),
      tv('17:00', 0.9),
      tv('18:00', 0.5),
      tv('24:00', 0.3),
    ],
    saturday: [tv('24:00', 0.3)],
    sunday: [tv('24:00', 0.3)],
    holiday: [tv('24:00', 0.3)],
  },
};

/**
 * Office heating setpoint schedule
 */
const officeHeatingSetpoint: ScheduleTemplate = {
  id: 'office-heating-setpoint',
  name: 'Office Heating Setpoint',
  description: 'Weekday 21C occupied, 15.6C setback',
  category: 'heating',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Temperature',
  source: 'ASHRAE 90.1',
  weekPattern: {
    monday: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
    tuesday: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
    wednesday: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
    thursday: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
    friday: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
    saturday: [tv('24:00', 15.6)],
    sunday: [tv('24:00', 15.6)],
    holiday: [tv('24:00', 15.6)],
    winterDesignDay: [tv('06:00', 15.6), tv('07:00', 21.0), tv('18:00', 21.0), tv('24:00', 15.6)],
  },
};

/**
 * Office cooling setpoint schedule
 */
const officeCoolingSetpoint: ScheduleTemplate = {
  id: 'office-cooling-setpoint',
  name: 'Office Cooling Setpoint',
  description: 'Weekday 24C occupied, 30C setup',
  category: 'cooling',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Temperature',
  source: 'ASHRAE 90.1',
  weekPattern: {
    monday: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
    tuesday: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
    wednesday: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
    thursday: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
    friday: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
    saturday: [tv('24:00', 30.0)],
    sunday: [tv('24:00', 30.0)],
    holiday: [tv('24:00', 30.0)],
    summerDesignDay: [tv('06:00', 30.0), tv('07:00', 24.0), tv('18:00', 24.0), tv('24:00', 30.0)],
  },
};

/**
 * HVAC availability - weekdays only
 */
const hvacWeekdaysOnly: ScheduleTemplate = {
  id: 'hvac-weekdays-only',
  name: 'HVAC Availability (Weekdays)',
  description: 'HVAC on during weekday occupied hours',
  category: 'hvac',
  buildingTypes: ['office', 'commercial', 'school'],
  typeLimits: 'On/Off',
  source: 'Standard Practice',
  weekPattern: {
    monday: [tv('06:00', 0), tv('07:00', 1), tv('19:00', 1), tv('24:00', 0)],
    tuesday: [tv('06:00', 0), tv('07:00', 1), tv('19:00', 1), tv('24:00', 0)],
    wednesday: [tv('06:00', 0), tv('07:00', 1), tv('19:00', 1), tv('24:00', 0)],
    thursday: [tv('06:00', 0), tv('07:00', 1), tv('19:00', 1), tv('24:00', 0)],
    friday: [tv('06:00', 0), tv('07:00', 1), tv('19:00', 1), tv('24:00', 0)],
    saturday: [tv('24:00', 0)],
    sunday: [tv('24:00', 0)],
    holiday: [tv('24:00', 0)],
  },
};

/**
 * Always On schedule
 */
const alwaysOn: ScheduleTemplate = {
  id: 'always-on',
  name: 'Always On',
  description: 'Constant value of 1.0 (24/7)',
  category: 'availability',
  buildingTypes: ['all'],
  typeLimits: 'On/Off',
  source: 'Standard',
  weekPattern: {
    monday: [tv('24:00', 1)],
    tuesday: [tv('24:00', 1)],
    wednesday: [tv('24:00', 1)],
    thursday: [tv('24:00', 1)],
    friday: [tv('24:00', 1)],
    saturday: [tv('24:00', 1)],
    sunday: [tv('24:00', 1)],
    holiday: [tv('24:00', 1)],
    summerDesignDay: [tv('24:00', 1)],
    winterDesignDay: [tv('24:00', 1)],
  },
};

/**
 * Always Off schedule
 */
const alwaysOff: ScheduleTemplate = {
  id: 'always-off',
  name: 'Always Off',
  description: 'Constant value of 0.0',
  category: 'availability',
  buildingTypes: ['all'],
  typeLimits: 'On/Off',
  source: 'Standard',
  weekPattern: {
    monday: [tv('24:00', 0)],
    tuesday: [tv('24:00', 0)],
    wednesday: [tv('24:00', 0)],
    thursday: [tv('24:00', 0)],
    friday: [tv('24:00', 0)],
    saturday: [tv('24:00', 0)],
    sunday: [tv('24:00', 0)],
    holiday: [tv('24:00', 0)],
    summerDesignDay: [tv('24:00', 0)],
    winterDesignDay: [tv('24:00', 0)],
  },
};

/**
 * School occupancy schedule
 */
const schoolOccupancy: ScheduleTemplate = {
  id: 'school-occupancy',
  name: 'School Occupancy',
  description: 'K-12 school hours 8am-3pm',
  category: 'occupancy',
  buildingTypes: ['school', 'education'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('07:00', 0),
      tv('08:00', 0.5),
      tv('08:30', 1.0),
      tv('11:30', 1.0),
      tv('12:00', 0.7),
      tv('12:30', 1.0),
      tv('15:00', 1.0),
      tv('15:30', 0.3),
      tv('18:00', 0.1),
      tv('24:00', 0),
    ],
    tuesday: [
      tv('07:00', 0),
      tv('08:00', 0.5),
      tv('08:30', 1.0),
      tv('11:30', 1.0),
      tv('12:00', 0.7),
      tv('12:30', 1.0),
      tv('15:00', 1.0),
      tv('15:30', 0.3),
      tv('18:00', 0.1),
      tv('24:00', 0),
    ],
    wednesday: [
      tv('07:00', 0),
      tv('08:00', 0.5),
      tv('08:30', 1.0),
      tv('11:30', 1.0),
      tv('12:00', 0.7),
      tv('12:30', 1.0),
      tv('15:00', 1.0),
      tv('15:30', 0.3),
      tv('18:00', 0.1),
      tv('24:00', 0),
    ],
    thursday: [
      tv('07:00', 0),
      tv('08:00', 0.5),
      tv('08:30', 1.0),
      tv('11:30', 1.0),
      tv('12:00', 0.7),
      tv('12:30', 1.0),
      tv('15:00', 1.0),
      tv('15:30', 0.3),
      tv('18:00', 0.1),
      tv('24:00', 0),
    ],
    friday: [
      tv('07:00', 0),
      tv('08:00', 0.5),
      tv('08:30', 1.0),
      tv('11:30', 1.0),
      tv('12:00', 0.7),
      tv('12:30', 1.0),
      tv('15:00', 1.0),
      tv('15:30', 0.3),
      tv('24:00', 0),
    ],
    saturday: [tv('24:00', 0)],
    sunday: [tv('24:00', 0)],
    holiday: [tv('24:00', 0)],
  },
};

/**
 * Retail store occupancy schedule
 */
const retailOccupancy: ScheduleTemplate = {
  id: 'retail-occupancy',
  name: 'Retail Store Occupancy',
  description: 'Standard retail hours 10am-9pm',
  category: 'occupancy',
  buildingTypes: ['retail', 'commercial'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('09:00', 0),
      tv('10:00', 0.3),
      tv('12:00', 0.7),
      tv('14:00', 0.5),
      tv('17:00', 0.8),
      tv('19:00', 1.0),
      tv('21:00', 0.5),
      tv('24:00', 0),
    ],
    tuesday: [
      tv('09:00', 0),
      tv('10:00', 0.3),
      tv('12:00', 0.7),
      tv('14:00', 0.5),
      tv('17:00', 0.8),
      tv('19:00', 1.0),
      tv('21:00', 0.5),
      tv('24:00', 0),
    ],
    wednesday: [
      tv('09:00', 0),
      tv('10:00', 0.3),
      tv('12:00', 0.7),
      tv('14:00', 0.5),
      tv('17:00', 0.8),
      tv('19:00', 1.0),
      tv('21:00', 0.5),
      tv('24:00', 0),
    ],
    thursday: [
      tv('09:00', 0),
      tv('10:00', 0.3),
      tv('12:00', 0.7),
      tv('14:00', 0.5),
      tv('17:00', 0.8),
      tv('19:00', 1.0),
      tv('21:00', 0.5),
      tv('24:00', 0),
    ],
    friday: [
      tv('09:00', 0),
      tv('10:00', 0.3),
      tv('12:00', 0.8),
      tv('14:00', 0.6),
      tv('17:00', 0.9),
      tv('19:00', 1.0),
      tv('21:00', 0.6),
      tv('24:00', 0),
    ],
    saturday: [
      tv('09:00', 0),
      tv('10:00', 0.5),
      tv('12:00', 1.0),
      tv('17:00', 1.0),
      tv('19:00', 0.8),
      tv('21:00', 0.5),
      tv('24:00', 0),
    ],
    sunday: [
      tv('10:00', 0),
      tv('11:00', 0.3),
      tv('13:00', 0.8),
      tv('17:00', 0.8),
      tv('18:00', 0.3),
      tv('24:00', 0),
    ],
    holiday: [tv('24:00', 0)],
  },
};

/**
 * Hospital 24/7 occupancy
 */
const hospital24x7: ScheduleTemplate = {
  id: 'hospital-24x7',
  name: 'Hospital (24/7)',
  description: '24-hour hospital operation',
  category: 'occupancy',
  buildingTypes: ['hospital', 'healthcare'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 1.0),
      tv('18:00', 1.0),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    tuesday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 1.0),
      tv('18:00', 1.0),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    wednesday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 1.0),
      tv('18:00', 1.0),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    thursday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 1.0),
      tv('18:00', 1.0),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    friday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 1.0),
      tv('18:00', 1.0),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    saturday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 0.9),
      tv('18:00', 0.9),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    sunday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 0.9),
      tv('18:00', 0.9),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    holiday: [
      tv('06:00', 0.7),
      tv('07:00', 0.8),
      tv('08:00', 0.9),
      tv('18:00', 0.9),
      tv('20:00', 0.8),
      tv('22:00', 0.7),
      tv('24:00', 0.7),
    ],
    summerDesignDay: [tv('24:00', 1.0)],
    winterDesignDay: [tv('24:00', 1.0)],
  },
};

/**
 * Restaurant occupancy schedule
 */
const restaurantOccupancy: ScheduleTemplate = {
  id: 'restaurant-occupancy',
  name: 'Restaurant Occupancy',
  description: 'Lunch and dinner service',
  category: 'occupancy',
  buildingTypes: ['restaurant', 'food-service'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.5),
      tv('17:00', 0.3),
      tv('18:00', 0.8),
      tv('20:00', 1.0),
      tv('22:00', 0.5),
      tv('24:00', 0),
    ],
    tuesday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.5),
      tv('17:00', 0.3),
      tv('18:00', 0.8),
      tv('20:00', 1.0),
      tv('22:00', 0.5),
      tv('24:00', 0),
    ],
    wednesday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.5),
      tv('17:00', 0.3),
      tv('18:00', 0.8),
      tv('20:00', 1.0),
      tv('22:00', 0.5),
      tv('24:00', 0),
    ],
    thursday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.5),
      tv('17:00', 0.3),
      tv('18:00', 0.8),
      tv('20:00', 1.0),
      tv('22:00', 0.5),
      tv('24:00', 0),
    ],
    friday: [
      tv('06:00', 0),
      tv('07:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.5),
      tv('17:00', 0.3),
      tv('18:00', 1.0),
      tv('21:00', 1.0),
      tv('23:00', 0.5),
      tv('24:00', 0.2),
    ],
    saturday: [
      tv('07:00', 0),
      tv('08:00', 0.1),
      tv('11:00', 0.3),
      tv('12:00', 1.0),
      tv('14:00', 0.6),
      tv('17:00', 0.4),
      tv('18:00', 1.0),
      tv('21:00', 1.0),
      tv('23:00', 0.5),
      tv('24:00', 0.2),
    ],
    sunday: [
      tv('08:00', 0),
      tv('09:00', 0.2),
      tv('11:00', 0.5),
      tv('13:00', 1.0),
      tv('15:00', 0.6),
      tv('17:00', 0.4),
      tv('18:00', 0.8),
      tv('20:00', 0.8),
      tv('21:00', 0.3),
      tv('24:00', 0),
    ],
    holiday: [tv('24:00', 0)],
  },
};

/**
 * Residential occupancy schedule
 */
const residentialOccupancy: ScheduleTemplate = {
  id: 'residential-occupancy',
  name: 'Residential Occupancy',
  description: 'Typical home occupancy pattern',
  category: 'occupancy',
  buildingTypes: ['residential', 'apartment', 'house'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 1.0),
      tv('07:00', 0.9),
      tv('08:00', 0.4),
      tv('17:00', 0.4),
      tv('18:00', 0.7),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    tuesday: [
      tv('06:00', 1.0),
      tv('07:00', 0.9),
      tv('08:00', 0.4),
      tv('17:00', 0.4),
      tv('18:00', 0.7),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    wednesday: [
      tv('06:00', 1.0),
      tv('07:00', 0.9),
      tv('08:00', 0.4),
      tv('17:00', 0.4),
      tv('18:00', 0.7),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    thursday: [
      tv('06:00', 1.0),
      tv('07:00', 0.9),
      tv('08:00', 0.4),
      tv('17:00', 0.4),
      tv('18:00', 0.7),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    friday: [
      tv('06:00', 1.0),
      tv('07:00', 0.9),
      tv('08:00', 0.4),
      tv('17:00', 0.4),
      tv('18:00', 0.7),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    saturday: [
      tv('08:00', 1.0),
      tv('09:00', 0.9),
      tv('11:00', 0.6),
      tv('18:00', 0.6),
      tv('20:00', 0.8),
      tv('24:00', 1.0),
    ],
    sunday: [
      tv('08:00', 1.0),
      tv('09:00', 0.9),
      tv('18:00', 0.9),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
    holiday: [
      tv('08:00', 1.0),
      tv('09:00', 0.9),
      tv('18:00', 0.9),
      tv('21:00', 0.9),
      tv('24:00', 1.0),
    ],
  },
};

/**
 * Residential heating setpoint
 */
const residentialHeatingSetpoint: ScheduleTemplate = {
  id: 'residential-heating-setpoint',
  name: 'Residential Heating Setpoint',
  description: '20C occupied, 18C sleep, 15.6C away',
  category: 'heating',
  buildingTypes: ['residential', 'apartment', 'house'],
  typeLimits: 'Temperature',
  source: 'ASHRAE 90.2',
  weekPattern: {
    monday: [
      tv('06:00', 18.0),
      tv('07:00', 20.0),
      tv('08:00', 15.6),
      tv('17:00', 15.6),
      tv('18:00', 20.0),
      tv('22:00', 20.0),
      tv('24:00', 18.0),
    ],
    tuesday: [
      tv('06:00', 18.0),
      tv('07:00', 20.0),
      tv('08:00', 15.6),
      tv('17:00', 15.6),
      tv('18:00', 20.0),
      tv('22:00', 20.0),
      tv('24:00', 18.0),
    ],
    wednesday: [
      tv('06:00', 18.0),
      tv('07:00', 20.0),
      tv('08:00', 15.6),
      tv('17:00', 15.6),
      tv('18:00', 20.0),
      tv('22:00', 20.0),
      tv('24:00', 18.0),
    ],
    thursday: [
      tv('06:00', 18.0),
      tv('07:00', 20.0),
      tv('08:00', 15.6),
      tv('17:00', 15.6),
      tv('18:00', 20.0),
      tv('22:00', 20.0),
      tv('24:00', 18.0),
    ],
    friday: [
      tv('06:00', 18.0),
      tv('07:00', 20.0),
      tv('08:00', 15.6),
      tv('17:00', 15.6),
      tv('18:00', 20.0),
      tv('22:00', 20.0),
      tv('24:00', 18.0),
    ],
    saturday: [tv('08:00', 18.0), tv('09:00', 20.0), tv('22:00', 20.0), tv('24:00', 18.0)],
    sunday: [tv('08:00', 18.0), tv('09:00', 20.0), tv('22:00', 20.0), tv('24:00', 18.0)],
    holiday: [tv('08:00', 18.0), tv('09:00', 20.0), tv('22:00', 20.0), tv('24:00', 18.0)],
    winterDesignDay: [tv('24:00', 20.0)],
  },
};

/**
 * Residential cooling setpoint
 */
const residentialCoolingSetpoint: ScheduleTemplate = {
  id: 'residential-cooling-setpoint',
  name: 'Residential Cooling Setpoint',
  description: '24C occupied, 26C sleep, 30C away',
  category: 'cooling',
  buildingTypes: ['residential', 'apartment', 'house'],
  typeLimits: 'Temperature',
  source: 'ASHRAE 90.2',
  weekPattern: {
    monday: [
      tv('06:00', 26.0),
      tv('07:00', 24.0),
      tv('08:00', 30.0),
      tv('17:00', 30.0),
      tv('18:00', 24.0),
      tv('22:00', 24.0),
      tv('24:00', 26.0),
    ],
    tuesday: [
      tv('06:00', 26.0),
      tv('07:00', 24.0),
      tv('08:00', 30.0),
      tv('17:00', 30.0),
      tv('18:00', 24.0),
      tv('22:00', 24.0),
      tv('24:00', 26.0),
    ],
    wednesday: [
      tv('06:00', 26.0),
      tv('07:00', 24.0),
      tv('08:00', 30.0),
      tv('17:00', 30.0),
      tv('18:00', 24.0),
      tv('22:00', 24.0),
      tv('24:00', 26.0),
    ],
    thursday: [
      tv('06:00', 26.0),
      tv('07:00', 24.0),
      tv('08:00', 30.0),
      tv('17:00', 30.0),
      tv('18:00', 24.0),
      tv('22:00', 24.0),
      tv('24:00', 26.0),
    ],
    friday: [
      tv('06:00', 26.0),
      tv('07:00', 24.0),
      tv('08:00', 30.0),
      tv('17:00', 30.0),
      tv('18:00', 24.0),
      tv('22:00', 24.0),
      tv('24:00', 26.0),
    ],
    saturday: [tv('08:00', 26.0), tv('09:00', 24.0), tv('22:00', 24.0), tv('24:00', 26.0)],
    sunday: [tv('08:00', 26.0), tv('09:00', 24.0), tv('22:00', 24.0), tv('24:00', 26.0)],
    holiday: [tv('08:00', 26.0), tv('09:00', 24.0), tv('22:00', 24.0), tv('24:00', 26.0)],
    summerDesignDay: [tv('24:00', 24.0)],
  },
};

/**
 * Warehouse occupancy schedule
 */
const warehouseOccupancy: ScheduleTemplate = {
  id: 'warehouse-occupancy',
  name: 'Warehouse Occupancy',
  description: 'Standard warehouse hours',
  category: 'occupancy',
  buildingTypes: ['warehouse', 'industrial'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [tv('06:00', 0), tv('07:00', 0.8), tv('17:00', 0.8), tv('18:00', 0.1), tv('24:00', 0)],
    tuesday: [tv('06:00', 0), tv('07:00', 0.8), tv('17:00', 0.8), tv('18:00', 0.1), tv('24:00', 0)],
    wednesday: [tv('06:00', 0), tv('07:00', 0.8), tv('17:00', 0.8), tv('18:00', 0.1), tv('24:00', 0)],
    thursday: [tv('06:00', 0), tv('07:00', 0.8), tv('17:00', 0.8), tv('18:00', 0.1), tv('24:00', 0)],
    friday: [tv('06:00', 0), tv('07:00', 0.8), tv('17:00', 0.8), tv('18:00', 0.1), tv('24:00', 0)],
    saturday: [tv('24:00', 0)],
    sunday: [tv('24:00', 0)],
    holiday: [tv('24:00', 0)],
  },
};

/**
 * Activity level schedule (W/person)
 */
const officeActivity: ScheduleTemplate = {
  id: 'office-activity',
  name: 'Office Activity Level',
  description: 'Office activity level in W/person',
  category: 'activity',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Any Number',
  source: 'ASHRAE Standard 55',
  weekPattern: {
    monday: [tv('24:00', 120)],
    tuesday: [tv('24:00', 120)],
    wednesday: [tv('24:00', 120)],
    thursday: [tv('24:00', 120)],
    friday: [tv('24:00', 120)],
    saturday: [tv('24:00', 120)],
    sunday: [tv('24:00', 120)],
    holiday: [tv('24:00', 120)],
  },
};

/**
 * Infiltration schedule
 */
const officeInfiltration: ScheduleTemplate = {
  id: 'office-infiltration',
  name: 'Office Infiltration',
  description: 'Reduced infiltration during HVAC operation',
  category: 'ventilation',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Fraction',
  source: 'Standard Practice',
  weekPattern: {
    monday: [tv('06:00', 1.0), tv('07:00', 0.25), tv('19:00', 0.25), tv('24:00', 1.0)],
    tuesday: [tv('06:00', 1.0), tv('07:00', 0.25), tv('19:00', 0.25), tv('24:00', 1.0)],
    wednesday: [tv('06:00', 1.0), tv('07:00', 0.25), tv('19:00', 0.25), tv('24:00', 1.0)],
    thursday: [tv('06:00', 1.0), tv('07:00', 0.25), tv('19:00', 0.25), tv('24:00', 1.0)],
    friday: [tv('06:00', 1.0), tv('07:00', 0.25), tv('19:00', 0.25), tv('24:00', 1.0)],
    saturday: [tv('24:00', 1.0)],
    sunday: [tv('24:00', 1.0)],
    holiday: [tv('24:00', 1.0)],
  },
};

/**
 * Hot water use schedule
 */
const officeHotWater: ScheduleTemplate = {
  id: 'office-hot-water',
  name: 'Office Hot Water',
  description: 'Service hot water use schedule',
  category: 'equipment',
  buildingTypes: ['office', 'commercial'],
  typeLimits: 'Fraction',
  source: 'DOE Reference Buildings',
  weekPattern: {
    monday: [
      tv('06:00', 0.05),
      tv('07:00', 0.1),
      tv('08:00', 0.3),
      tv('09:00', 0.15),
      tv('12:00', 0.15),
      tv('13:00', 0.2),
      tv('14:00', 0.1),
      tv('17:00', 0.1),
      tv('18:00', 0.15),
      tv('20:00', 0.05),
      tv('24:00', 0.05),
    ],
    tuesday: [
      tv('06:00', 0.05),
      tv('07:00', 0.1),
      tv('08:00', 0.3),
      tv('09:00', 0.15),
      tv('12:00', 0.15),
      tv('13:00', 0.2),
      tv('14:00', 0.1),
      tv('17:00', 0.1),
      tv('18:00', 0.15),
      tv('20:00', 0.05),
      tv('24:00', 0.05),
    ],
    wednesday: [
      tv('06:00', 0.05),
      tv('07:00', 0.1),
      tv('08:00', 0.3),
      tv('09:00', 0.15),
      tv('12:00', 0.15),
      tv('13:00', 0.2),
      tv('14:00', 0.1),
      tv('17:00', 0.1),
      tv('18:00', 0.15),
      tv('20:00', 0.05),
      tv('24:00', 0.05),
    ],
    thursday: [
      tv('06:00', 0.05),
      tv('07:00', 0.1),
      tv('08:00', 0.3),
      tv('09:00', 0.15),
      tv('12:00', 0.15),
      tv('13:00', 0.2),
      tv('14:00', 0.1),
      tv('17:00', 0.1),
      tv('18:00', 0.15),
      tv('20:00', 0.05),
      tv('24:00', 0.05),
    ],
    friday: [
      tv('06:00', 0.05),
      tv('07:00', 0.1),
      tv('08:00', 0.3),
      tv('09:00', 0.15),
      tv('12:00', 0.15),
      tv('13:00', 0.2),
      tv('14:00', 0.1),
      tv('17:00', 0.1),
      tv('18:00', 0.15),
      tv('20:00', 0.05),
      tv('24:00', 0.05),
    ],
    saturday: [tv('24:00', 0.05)],
    sunday: [tv('24:00', 0.05)],
    holiday: [tv('24:00', 0.05)],
  },
};

/**
 * Exterior lighting schedule
 */
const exteriorLighting: ScheduleTemplate = {
  id: 'exterior-lighting',
  name: 'Exterior Lighting',
  description: 'Dusk to dawn lighting schedule',
  category: 'lighting',
  buildingTypes: ['all'],
  typeLimits: 'Fraction',
  source: 'Standard Practice',
  weekPattern: {
    monday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    tuesday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    wednesday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    thursday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    friday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    saturday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    sunday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
    holiday: [tv('06:00', 1.0), tv('07:00', 0), tv('18:00', 0), tv('19:00', 1.0), tv('24:00', 1.0)],
  },
};

/**
 * Fraction of 0.5 constant
 */
const fractionHalf: ScheduleTemplate = {
  id: 'fraction-half',
  name: 'Fraction 0.5',
  description: 'Constant value of 0.5',
  category: 'availability',
  buildingTypes: ['all'],
  typeLimits: 'Fraction',
  source: 'Standard',
  weekPattern: {
    monday: [tv('24:00', 0.5)],
    tuesday: [tv('24:00', 0.5)],
    wednesday: [tv('24:00', 0.5)],
    thursday: [tv('24:00', 0.5)],
    friday: [tv('24:00', 0.5)],
    saturday: [tv('24:00', 0.5)],
    sunday: [tv('24:00', 0.5)],
    holiday: [tv('24:00', 0.5)],
  },
};

/**
 * All schedule templates
 */
export const scheduleTemplates: ScheduleTemplate[] = [
  // Occupancy
  officeOccupancyStandard,
  schoolOccupancy,
  retailOccupancy,
  hospital24x7,
  restaurantOccupancy,
  residentialOccupancy,
  warehouseOccupancy,

  // Lighting
  officeLighting,
  exteriorLighting,

  // Equipment
  officeEquipment,
  officeHotWater,

  // HVAC & Setpoints
  officeHeatingSetpoint,
  officeCoolingSetpoint,
  residentialHeatingSetpoint,
  residentialCoolingSetpoint,
  hvacWeekdaysOnly,

  // Ventilation
  officeInfiltration,

  // Activity
  officeActivity,

  // General/Availability
  alwaysOn,
  alwaysOff,
  fractionHalf,
];

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: string): ScheduleTemplate[] {
  return scheduleTemplates.filter((t) => t.category === category);
}

/**
 * Get templates by building type
 */
export function getTemplatesByBuildingType(buildingType: string): ScheduleTemplate[] {
  return scheduleTemplates.filter(
    (t) => t.buildingTypes.includes(buildingType) || t.buildingTypes.includes('all')
  );
}

/**
 * Search templates by name or description
 */
export function searchTemplates(query: string): ScheduleTemplate[] {
  const lowerQuery = query.toLowerCase();
  return scheduleTemplates.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.category.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get a template by ID
 */
export function getTemplateById(id: string): ScheduleTemplate | undefined {
  return scheduleTemplates.find((t) => t.id === id);
}
